<template>
  <modal
    ref="modal"
    @closed="destroySetup"
    @exited="destroySetup"
    :showOnCreate="showOnCreate"
    :title="modalTitle"
    :width="isNewAudienceStep ? 600 : 0"
    closeButton
    :hideActions="!showButtons"
  >
    <template v-if="!isDataStep && !isDuplicate && !isUpdate && !isUpdateCampaign && !isViewingSegmentInfo && showBackButton" slot="header-buttons">
      <back-arrow-nav v-if="!isNewAudienceStep" class="las-back-button" @goBack="goBack"/>
    </template>
    <template>
      <div class="stepper">
        <data-source-step
          v-if="isDataStep"
          :targetName="titleInfo.targetName"
          v-model="dataInfo"/>
        <finish-step
          v-else-if="isFinishStep"
          :targetName="titleInfo.targetName"
          :selectedDataSource="selectedDataSource"
          :onComplete="stepperOnComplete"
          v-model="finishInfo"
          @close="close"
        />
        <create-audience
          v-else-if="isNewAudienceStep"
          v-model="audienceInfo"
          :onComplete="stepperOnComplete"
          :onAudienceActivation="onAudienceActivation"
        />
      </div>
    </template>
    <template v-if="showButtons" slot="actions">
      <action-button
        :disabled="disableBackButton"
        @click.native="cancelButton"
        label="cancel"
        color="secondary"
        id="datasourceModalCancelBtn"
      />
      <action-button
        :disabled="!completedCurrentStep"
        @click.native="nextButton"
        label="Next"
        color="success"
        id="datasourceModalNextBtn"
      />
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/common/modal'
import DataSourceStep from './DataSourceStep'
import FinishStep from './FinishStep'
import CreateAudience from '@/appComponents/targets/createTarget/forms/CreateAudience'
import InteractiveButton from '@/components/common/simpleButton/InteractiveSimpleButton'
import ActionButton from '@/components/common/actionButton/ActionButton'
import DialogModal from '@/components/common/modal/DialogModal'
import {TARGET_ACTIONS, INTENTS} from '@/appLogic/targets/Constants'
import BackArrowNav from '@/components/common/backArrowNav/BackArrowNav'
import {
  SET_SHOW_MODAL_STEPPER,
  SET_STEPPER_DATA_SOURCE,
  SET_UPDATING_TARGET,
  UPLOAD_MODAL_SET_APPEND_TYPE,
  SET_UPLOAD_MODAL_OPTIONS,
  SET_INTENTS
} from '@/appLogic/targets/createTarget/store/actions'

let STEPS = {
  TITLE: 1,
  DATA: 2,
  NEW_AUDIENCE: 3,
  FINISH: 4,
}

export default {
  components: {
    Modal,
    DataSourceStep,
    FinishStep,
    InteractiveButton,
    ActionButton,
    DialogModal,
    BackArrowNav,
    CreateAudience
  },
  props: {
    showOnCreate: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentStep: 0,
      previousStep: 0,
      modalTitle: '',
      titleInfo: {},
      dataInfo: {},
      finishInfo: {},
      audienceInfo: {},
      selectedDataSource: null,
      isActivatingAudience: false,
    }
  },
  mounted () {
    this.registerWithDataSource()
  },
  computed: {
    nielsenDatasources () {
      return this.$store.getters.getNielsenDatasources
    },
    showStepperModalTrigger () {
      return this.$store.state.createTarget.showStepperModal
    },
    isTitleStep () {
      return this.currentStep === STEPS.TITLE
    },
    isDataStep () {
      return this.currentStep === STEPS.DATA
    },
    isFinishStep () {
      return this.currentStep === STEPS.FINISH
    },
    isNewAudienceStep () {
      return this.currentStep === STEPS.NEW_AUDIENCE
    },
    completedCurrentStep () {
      if (this.isTitleStep) {
        return this.titleInfo.completed
      } else if (this.isDataStep) {
        return this.dataInfo.completed
      } else {
        return this.finishInfo.completed
      }
    },
    disableBackButton () {
      return STEPS.TITLE === this.currentStep
    },
    showButtons () {
      return this.currentStep < STEPS.NEW_AUDIENCE
    },
    selectedDataSourceTrigger () {
      return this.$store.state.createTarget.stepperDataSourceTrigger
    },
    isDuplicate () {
      return this.$store.state.createTarget.initialTargetAction === TARGET_ACTIONS.DUPLICATE
    },
    isUpdate () {
      return this.$store.state.createTarget.initialTargetAction === TARGET_ACTIONS.UPDATE
    },
    isUpdateCampaign () {
      return this.$store.state.createTarget.initialTargetAction === TARGET_ACTIONS.UPDATE_CAMPAIGN
    },
    isViewingSegmentInfo () {
      return this.$store.state.createTarget.initialTargetAction === TARGET_ACTIONS.SEGMENT_INFO
    },
    userPermissions () {
      return this.$store.state.auth.permissions
    },
    hasAudienceDiscoveryAccess () {
      return this.$store.state.createTarget.intents.includes(INTENTS['Marketing']['Name']) || this.$store.state.createTarget.intents.includes(INTENTS['Research']['Name'])
    },
    hasLASWorkbenchAccess () {
      return this.$store.state.createTarget.intents.includes(INTENTS['Sales']['Name'])
    },
    showBackButton () {
      return this.selectedDataSourceTrigger !== this.nielsenDatasources.FILE
    }
  },
  methods: {
    open () {
      this.$refs.modal.open()
    },
    close () {
      if (this.$refs.modal) {
        this.$refs.modal.close()
      }
    },
    destroySetup () {
      this.$store.dispatch(SET_SHOW_MODAL_STEPPER, {step: 0})
      this.$store.dispatch(SET_STEPPER_DATA_SOURCE, {option: null})
      if (!this.isActivatingAudience) {
        this.$store.dispatch(SET_UPDATING_TARGET, null)
      }
      this.$store.dispatch(UPLOAD_MODAL_SET_APPEND_TYPE, null)
      this.$store.dispatch(SET_UPLOAD_MODAL_OPTIONS, null)
      this.$store.dispatch(SET_INTENTS, null)
      Object.assign(this.$data, this.$options.data.apply(this))
    },
    goBack () {
      this.decrementStep()
    },
    incrementStep () {
      this.previousStep = this.currentStep
      if (this.isDataStep || this.isNewAudienceStep) {
        this.currentStep = STEPS.FINISH
      } else {
        this.currentStep = this.currentStep + 1
      }
    },
    decrementStep () {
      if (this.currentStep !== this.previousStep) {
        this.currentStep = this.previousStep
      } else {
        this.currentStep = this.currentStep - 1
      }
      this.previousStep = this.currentStep - 1
    },
    backButton () {
      this.decrementStep()
    },
    cancelButton () {
      this.close()
    },
    nextButton () {
      if (this.isDataStep && this.dataInfo.selectedDataSource === this.nielsenDatasources.AMRLD && this.hasAudienceDiscoveryAccess) {
        this.selectedDataSource = this.nielsenDatasources.AUDIENCE_DISCOVERY
      } else if (this.isDataStep && this.dataInfo.selectedDataSource === this.nielsenDatasources.AMRLD && this.hasLASWorkbenchAccess) {
        this.dataInfo.selectedDataSource = this.nielsenDatasources.AMRLD
        this.selectedDataSource = this.nielsenDatasources.AMRLD
      }
      this.incrementStep()
    },
    registerWithDataSource () {
      if (this.selectedDataSourceTrigger === this.nielsenDatasources.SELECT_A_NEW_DATA_SOURCE || this.selectedDataSourceTrigger === null) {
        this.currentStep = STEPS.DATA
      } else {
        this.currentStep = STEPS.FINISH
        this.selectedDataSource = this.selectedDataSourceTrigger
      }
      this.showedADprompt = false
    },
    stepperOnComplete () {
      this.close()
      this.$emit('onComplete')
    },
    onAudienceActivation () {
      this.isActivatingAudience = true
      this.stepperOnComplete()
    }
  },
  watch: {
    showStepperModalTrigger () {
      this.currentStep = this.showStepperModalTrigger
      if (this.showStepperModalTrigger) {
        if (this.showStepperModalTrigger === STEPS.FINISH) {
          this.registerWithDataSource()
        }
        this.open()
      }
    },
    selectedDataSourceTrigger () {
      this.registerWithDataSource()
    },
    'dataInfo.selectedDataSource' () {
      this.selectedDataSource = this.dataInfo.selectedDataSource
    },
    'audienceInfo.selectedDataSource' () {
      if (this.audienceInfo.selectedDataSource === this.nielsenDatasources.FILE) {
        this.selectedDataSource = this.audienceInfo.selectedDataSource
        this.incrementStep()
      }
    },
    currentStep (step) {
      switch (step) {
        case STEPS.TITLE:
          this.modalTitle = 'Enter a title'
          break
        case STEPS.DATA:
          this.modalTitle = 'Select Data Source To Build A Target'
          break
        case STEPS.FINISH:
          this.modalTitle = ''
          break
        case STEPS.NEW_AUDIENCE:
          this.modalTitle = 'New Audience'
          this.audienceInfo = {}
          break
      }
    }
  },
  beforeDestroy () {
    this.destroySetup()
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// StepperModal.vue?0eb21812